import { Zip, ZipDeflate, ZipPassThrough, strToU8 } from 'fflate';

const clampCompressionLevel = (value) => {
  if (!Number.isFinite(value)) {
    return 6;
  }

  return Math.min(9, Math.max(0, Math.round(value)));
};

const toUint8Array = (content) => {
  if (content instanceof Uint8Array) {
    return content;
  }

  if (content instanceof ArrayBuffer) {
    return new Uint8Array(content);
  }

  if (ArrayBuffer.isView(content)) {
    return new Uint8Array(content.buffer, content.byteOffset, content.byteLength);
  }

  if (typeof content === 'string') {
    return strToU8(content);
  }

  return strToU8(String(content ?? ''));
};

class ZipPackagingService {
  supportsStreamingZip(fileSystemService) {
    return Boolean(fileSystemService?.supportsPackageWritableStreams?.());
  }

  /**
   * Write ZIP output directly into a file writable stream to avoid giant in-memory blobs.
   */
  async writeStreamingZip({
    writable,
    entries,
    compressionLevel = 6,
    onEntryComplete
  }) {
    if (!writable || typeof writable.write !== 'function') {
      throw new Error('A writable package stream is required for streaming ZIP creation.');
    }

    const normalizedLevel = clampCompressionLevel(compressionLevel);

    return await new Promise((resolve, reject) => {
      let settled = false;
      let writeChain = Promise.resolve();

      const settleReject = async (error) => {
        if (settled) {
          return;
        }

        settled = true;
        try {
          await writable.abort?.(error);
        } catch (abortError) {
          // Ignore abort cleanup errors and keep original failure.
        }

        reject(error instanceof Error ? error : new Error(String(error)));
      };

      const zip = new Zip((error, chunk, final) => {
        if (error) {
          settleReject(error);
          return;
        }

        writeChain = writeChain.then(() => writable.write(chunk));

        if (final) {
          writeChain
            .then(() => writable.close())
            .then(() => {
              if (!settled) {
                settled = true;
                resolve(true);
              }
            })
            .catch(settleReject);
        }
      });

      const run = async () => {
        try {
          for await (const entry of entries) {
            const entryName = String(entry?.path || '').trim();
            if (!entryName) {
              throw new Error('Streaming ZIP entry is missing a valid path.');
            }

            const entryBytes = toUint8Array(entry.content);
            const zipEntry = entry.store
              ? new ZipPassThrough(entryName)
              : new ZipDeflate(entryName, { level: normalizedLevel });

            zip.add(zipEntry);
            zipEntry.push(entryBytes, true);
            onEntryComplete?.({ path: entryName, sizeInBytes: entryBytes.byteLength });
          }

          zip.end();
        } catch (error) {
          settleReject(error);
        }
      };

      run();
    });
  }
}

export default ZipPackagingService;

